import React from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Chip,
    IconButton,
    LinearProgress,
    Grid,
    Link,
    Divider
} from '@mui/material';
import {
    PlayArrow,
    OpenInNew,
    QueueMusic,
    TrendingUp,
    MusicNote,
    Mood
} from '@mui/icons-material';
import { ArtistTrack, SonicProfile } from '../hooks/useArtistTracks';

// Helper function for formatting numbers
const formatNumber = (num?: number): string => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

interface TopTracksProps {
    tracks: ArtistTrack[];
    loading: boolean;
}

export const TopTracksComponent: React.FC<TopTracksProps> = ({ tracks, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Top Tracks
                    </Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (tracks.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Top Tracks
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        No track data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const topTrack = tracks[0];

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Top Tracks
                </Typography>
                
                {/* Featured Top Track */}
                <Box 
                    display="flex" 
                    alignItems="center" 
                    mb={2}
                    p={2}
                    bgcolor="grey.50"
                    borderRadius={1}
                >
                    <PlayArrow color="primary" sx={{ mr: 1, fontSize: 30 }} />
                    <Box flex={1}>
                        <Typography variant="h6" fontWeight="bold">
                            {topTrack.track_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {formatNumber(topTrack.spotify_plays)} plays • {topTrack.total_playlists} playlists
                        </Typography>
                        <Box display="flex" gap={1} mt={1}>
                            {topTrack.mood && (
                                <Chip 
                                    label={topTrack.mood} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {topTrack.genres.slice(0, 2).map((genre, idx) => (
                                <Chip 
                                    key={idx}
                                    label={genre} 
                                    size="small" 
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                    {topTrack.spotify_url && (
                        <IconButton 
                            onClick={() => {
                                // Convert https://open.spotify.com/track/id to spotify:track:id for app
                                const spotifyUri = topTrack.spotify_url.replace('https://open.spotify.com/', 'spotify:').replace(/\//g, ':');
                                window.open(spotifyUri, '_self');
                            }}
                            color="primary"
                            title="Play in Spotify"
                        >
                            <OpenInNew />
                        </IconButton>
                    )}
                </Box>

                {/* Additional Tracks */}
                {tracks.slice(1, 4).map((track, index) => (
                    <Box key={track.cm_track_id || index} mb={1}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" fontWeight="medium">
                                    {track.track_name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {formatNumber(track.spotify_plays)} plays
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip 
                                    label={`${track.total_playlists} playlists`}
                                    size="small"
                                    variant="outlined"
                                />
                                {track.spotify_url && (
                                    <IconButton 
                                        size="small"
                                        onClick={() => {
                                            // Convert https://open.spotify.com/track/id to spotify:track:id for app
                                            const spotifyUri = track.spotify_url.replace('https://open.spotify.com/', 'spotify:').replace(/\//g, ':');
                                            window.open(spotifyUri, '_self');
                                        }}
                                        title="Play in Spotify"
                                    >
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                        {index < tracks.slice(1, 4).length - 1 && <Divider sx={{ mt: 1 }} />}
                    </Box>
                ))}

                {/* Summary Stats */}
                <Box mt={2} pt={2} borderTop={1} borderColor="grey.200">
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Total Tracks
                            </Typography>
                            <Typography variant="h6">
                                {tracks.length}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Total Plays
                            </Typography>
                            <Typography variant="h6">
                                {formatNumber(tracks.reduce((sum, t) => sum + t.spotify_plays, 0))}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Avg Playlists
                            </Typography>
                            <Typography variant="h6">
                                {Math.round(tracks.reduce((sum, t) => sum + t.total_playlists, 0) / tracks.length)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    );
};

interface SonicProfileProps {
    sonicProfile: SonicProfile | null;
    loading: boolean;
}

export const SonicProfileComponent: React.FC<SonicProfileProps> = ({ sonicProfile, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Sonic Profile
                    </Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (!sonicProfile) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Sonic Profile
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        No sonic data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Sonic Profile
                </Typography>
                
                <Grid container spacing={3}>
                    {/* Dominant Moods */}
                    <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Mood color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Dominant Moods
                            </Typography>
                        </Box>
                        {sonicProfile.dominant_moods.length > 0 ? (
                            sonicProfile.dominant_moods.map((mood, index) => (
                                <Box key={mood.mood} mb={1}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2">
                                            {mood.mood}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {mood.count} track{mood.count !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={(mood.count / sonicProfile.total_tracks) * 100}
                                        sx={{ height: 4, borderRadius: 2 }}
                                    />
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No mood data
                            </Typography>
                        )}
                    </Grid>

                    {/* Dominant Genres */}
                    <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <MusicNote color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Genres
                            </Typography>
                        </Box>
                        {sonicProfile.dominant_genres.length > 0 ? (
                            sonicProfile.dominant_genres.map((genre, index) => (
                                <Box key={genre.genre} mb={1}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2">
                                            {genre.genre}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {genre.count} track{genre.count !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={(genre.count / sonicProfile.total_tracks) * 100}
                                        sx={{ height: 4, borderRadius: 2 }}
                                        color="secondary"
                                    />
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No genre data
                            </Typography>
                        )}
                    </Grid>

                    {/* Performance Stats */}
                    <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <TrendingUp color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Performance
                            </Typography>
                        </Box>
                        
                        <Box mb={2}>
                            <Typography variant="body2" color="textSecondary">
                                Editorial Playlist Ratio
                            </Typography>
                            <Typography variant="h6">
                                {(sonicProfile.editorial_playlist_ratio * 100).toFixed(1)}%
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={sonicProfile.editorial_playlist_ratio * 100}
                                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                                color="success"
                            />
                        </Box>

                        <Box>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Total Catalog
                            </Typography>
                            <Typography variant="body1">
                                <strong>{sonicProfile.total_tracks}</strong> tracks
                            </Typography>
                            <Typography variant="body1">
                                <strong>{formatNumber(sonicProfile.total_plays)}</strong> total plays
                            </Typography>
                            <Typography variant="body1">
                                <strong>{sonicProfile.total_playlists}</strong> playlist placements
                            </Typography>
                        </Box>

                        {/* Activities */}
                        {sonicProfile.dominant_activities.length > 0 && (
                            <Box mt={2}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                    Activities
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {sonicProfile.dominant_activities.slice(0, 3).map((activity, index) => (
                                        <Chip 
                                            key={activity.activity}
                                            label={activity.activity}
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};